import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import { AxiosResponse } from "axios";
import qs from "qs";
import Choice from "./Choice";
import FileAction from "./FileAction";
import Opinion from "./Opinion";
import Poll from "./Poll";
import TextAction from "./TextAction";
import Interactive from "./Interactive";
import AnswerStateModal from "./AnswerStateModal";
import { QuestionPreview } from "../../../../stores/CoursePreviewStore/Types";
import { Answer, Enrolment } from "../../../../stores/EnrolmentStore/Types";
import { getRequest, postRequest } from "../../../../utils/httpRequest";
import { errorNotification } from "../../../../utils/helpers";
import { nextLessonPath } from "../../helpers";
import { Button, Confirm } from "semantic-ui-react";
import { CommentResource } from "../../../../stores/CommentStore/Types";

interface Props {
  mode: string;
  enrolment: Enrolment;
  question: QuestionPreview;
  lastLesson: QuestionPreview;
  answer: Answer;
  page: string;
  loadingAnswer: boolean;
  refreshAnswers: () => void;
  getRecentNotifications: () => void;
  getXp: () => void;
  currentUser: any;
  search?: string;
}

interface State {
  selectedAnswer: any;
  submitting: boolean;
  resetting: boolean;
  answerState: string;
  showModal: boolean;
  confirmReset: boolean;
  redirectUrl: string;
  imageUrl: string;
  enrolment: Enrolment;
  answerStateTagline: string;
  pollAnswerOptions: any;
  pollAnswerOptionsWithPercent: any;
  comments: CommentResource[];
  markerProfilePictureUrls: string[];
  usingLife: boolean;
}

// Set just before the page reloads after a life is used, so the learner is
// brought back to the question they were retrying and not left at the top.
const RETRY_KEY = "lesson-retry-question";

const RESULTS = {
  pass: {
    titles: [
      "Jolly good",
      "Bravo",
      "Splendid stuff",
      "Spot on",
      "Marvellous",
      "Top marks",
      "First rate",
      "Quite right",
      "Nicely done",
      "Capital work",
      "Tickety-boo",
      "Smashing",
      "Champion",
      "Bang on",
      "Well played",
      "Exemplary",
      "Rather good",
      "Genuinely impressive",
      "A triumph",
      "Faultless",
      "Beautifully judged",
      "Pure class",
      "Outstanding",
      "Nothing to fault",
      "Right on the money",
      "Sharp as a tack",
      "Clever you",
      "Gold star",
      "Take a bow",
      "Magnificent",
      "Textbook",
      "Nailed on",
      "Pitch perfect",
      "Jolly well done",
      "Hats off",
      "Bullseye",
      "Worthy of a knighthood",
      "Give that human a biscuit",
      "Eureka",
      "Top of the class",
      "Resoundingly correct",
      "A masterstroke",
      "Quietly brilliant",
      "Good show",
      "Cracking",
      "Immaculate",
      "You clever thing",
      "Right as rain",
      "Supremely sensible",
    ],
  },
  fail: {
    titles: [
      "Ah, not quite",
      "So close",
      "Bad luck",
      "A noble effort",
      "Nearly had it",
      "Not this time",
      "Back to the drawing board",
      "A valiant attempt",
      "The elephant won that round",
      "Foiled",
      "Close, but no cigar",
      "A worthy try",
      "Not your finest hour",
      "Onwards regardless",
      "A learning opportunity, surely",
      "Gallant, if wrong",
      "Pipped at the post",
      "Almost, almost",
      "Tantalisingly close",
      "A spirited guess",
      "Not quite cricket",
      "Chin up",
      "We have all been there",
      "Plot twist, wrong",
      "A brave punt",
      "Missed by a whisker",
      "Not today",
      "Stumped",
      "A respectable miss",
      "The path was sneaky",
      "Better luck imminently",
      "Dust yourself off",
      "A glorious near-miss",
      "Wrong, but charmingly so",
      "Foiled again",
      "Keep the faith",
      "A try worth trying",
      "Not wrong forever",
      "Slightly off the mark",
      "A teachable wobble",
      "No shame in that",
      "Round two awaits",
      "A confident wrong answer",
      "Swing and a miss",
      "Not the one",
      "Have another crack",
      "So very nearly",
      "The map had other ideas",
    ],
  },
  marking: {
    titles: [
      "We will mark this for you now",
      "Nicely answered. A human takes it from here.",
    ],
  },
};

const pickResultTitle = (key: keyof typeof RESULTS) => {
  const titles = RESULTS[key].titles;
  return titles[Math.floor(Math.random() * titles.length)];
};

class Assignment extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      redirectUrl: "",
      selectedAnswer: null,
      submitting: false,
      resetting: false,
      answerState: "",
      showModal: false,
      confirmReset: false,
      imageUrl: "",
      enrolment: props.enrolment,
      answerStateTagline: "",
      pollAnswerOptions: props.question?.answer_options,
      pollAnswerOptionsWithPercent:
        props.question?.answer_options_with_vote_percentages,
      comments: [],
      markerProfilePictureUrls: props.answer?.marker_profile_picture_urls || [],
      usingLife: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    this.getComments();
    this.returnToRetriedQuestion();
  }

  // A wrong answer can be changed for a life. The offer sits with the answer
  // itself: the result pop-up can be dismissed, and the reminder at the top of
  // the lesson is a long scroll away from the question.
  useLife = (e) => {
    e.preventDefault();

    const { answer, question } = this.props;

    if (!answer || this.state.usingLife) return;

    this.setState({ usingLife: true });

    postRequest(`/api/answers/${answer.id}/life_uses`, {})
      .then(() => {
        try {
          window.sessionStorage.setItem(RETRY_KEY, String(question?.id));
        } catch (_error) {
          // Private browsing: the learner lands at the top, as before.
        }
        window.location.reload();
      })
      .catch((error) => {
        this.setState({ usingLife: false });

        errorNotification(
          "There was an error with using lives",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  // The lesson is put together in stages after a reload, and each stage
  // starts again at the top. So the note is left in place for a few seconds
  // and every stage that finds it comes back down to the question.
  returnToRetriedQuestion = () => {
    const questionId = this.props.question?.id;
    let retried = null;

    try {
      retried = window.sessionStorage.getItem(RETRY_KEY);
    } catch (_error) {
      return;
    }

    if (!retried || !questionId) return;

    const forget = () => {
      try {
        window.sessionStorage.removeItem(RETRY_KEY);
      } catch (_error) {
        // Nothing to forget.
      }
    };

    if (retried !== String(questionId)) {
      forget();
      return;
    }

    // A jump, not a glide: the page has only just loaded, and what is above
    // the question is still settling, so it is done again a moment later.
    const jump = () =>
      this.assignment.current?.scrollIntoView({
        behavior: "instant" as ScrollBehavior,
        block: "start",
      });

    window.setTimeout(jump, 400);
    window.setTimeout(jump, 1500);
    window.setTimeout(forget, 4000);
  };

  getComments = () => {
    const { answer, mode } = this.props;

    if (mode === "Opinion") {
      getRequest(`/api/comments.json?answer_id=${answer?.id}`)
        .then((response: AxiosResponse<CommentResource[]>) => {
          this.setState({ comments: response.data }, () =>
            this.scrollToComment(),
          );
        })
        .catch((error) => {
          (window as any).honeybadger?.notify(error?.response?.data?.error);
        });
    }
  };

  scrollToComment = () => {
    const { search } = this.props;

    const queryParams = qs.parse(search, { ignoreQueryPrefix: true });

    if (queryParams?.scroll_to) {
      document
        .getElementById(`${queryParams?.scroll_to}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  updateSelectedAnswer = (selectedAnswer) => this.setState({ selectedAnswer });

  answerStateTagline = () => {
    const { answerState } = this.state;

    if (!answerState) {
      return "";
    }

    switch (answerState) {
      case "pending":
      case "submitted":
        return pickResultTitle("marking");
      case "passed":
        return pickResultTitle("pass");
      case "failed":
        return pickResultTitle("fail");
      default:
        return "Something went wrong";
    }
  };

  imageUrl = () => {
    const { answerState } = this.state;

    const { answer } = this.props;

    if (!answerState) {
      return "";
    }

    let base_url =
      "https://42courses-course-material.s3-us-west-1.amazonaws.com/answers/giphys";
    let flavors = answerState == "passed" ? this.range(70) : this.range(45);
    let flavor = answer
      ? answer.id % flavors.length
      : Math.floor(Math.random() * flavors.length);

    switch (answerState) {
      case "pending":
      case "submitted":
        return `${base_url}/team-pending.png`;
      case "passed":
        return `${base_url}/pass/passed-${flavor}.gif`;
      case "failed":
        return `${base_url}/fail/failed-${flavor}.gif`;
      default:
        return "Something went wrong";
    }
  };

  resetAnswer = (e) => {
    e.preventDefault();
    this.setState({ resetting: true, confirmReset: false });
    const {
      question,
      enrolment,
      answer,
      mode,
      page,
      refreshAnswers,
      getRecentNotifications,
      currentUser,
      search,
    } = this.props;

    return postRequest(
      `/api/enrolments/${enrolment?.id}/questions/${question?.id}/answer/reset`,
      {},
    )
      .then((response: AxiosResponse) => {
        this.setState({
          resetting: false,
          selectedAnswer: null,
          answerState: "",
          answerStateTagline: "",
          imageUrl: "",
        });
        refreshAnswers();
        getRecentNotifications();

        if (page === "answerUpvotes" && mode === "Opinion") {
          window.location.href = `/enrolments/${enrolment.id}/questions/${question.id}`;
        }
      })
      .catch((error) => {
        this.setState({ submitting: false });
        getRecentNotifications();
        errorNotification(
          "Error resetting answer. Please try again",
          JSON.stringify(error.response.data),
        );
      });
  };

  submitAnswer = (e) => {
    e.preventDefault();
    this.setState({ submitting: true });
    const { selectedAnswer } = this.state;
    const {
      mode,
      question,
      enrolment,
      refreshAnswers,
      getRecentNotifications,
      getXp,
    } = this.props;
    const answerData = () => {
      if (["Poll", "Choice"].includes(mode)) {
        return { answer_option_id: selectedAnswer };
      } else if (mode == "FileAction") {
        return { content_url: selectedAnswer };
      } else if (mode == "Opinion" && question.uploader_type) {
        return { content_url: selectedAnswer };
      } else {
        return { content: selectedAnswer };
      }
    };

    return postRequest(
      `/api/answers.json?enrolment_id=${enrolment?.id}&question_id=${question?.id}`,
      answerData(),
    )
      .then((response: AxiosResponse) => {
        if (mode === "Opinion") {
          this.setState({
            redirectUrl: `/enrolments/${enrolment?.id}/questions/${question.id}/answers/${response.data.id}/votes`,
          });
        } else if (mode === "Poll") {
          refreshAnswers();
          getRequest(
            `/api/courses/${enrolment?.course?.slug}/questions/${question.id}.json`,
          )
            .then((questionRes: AxiosResponse) => {
              this.setState({
                pollAnswerOptions: questionRes.data.answer_options,
                pollAnswerOptionsWithPercent:
                  questionRes.data.answer_options_with_vote_percentages,
                submitting: false,
                answerState: response.data.state,
                enrolment: response.data.enrolment,
                markerProfilePictureUrls:
                  response.data.marker_profile_picture_urls || [],
              });

              if (
                ["passed", "failed"].includes(response.data.enrolment.state)
              ) {
                this.setState({
                  answerStateTagline: this.answerStateTagline(),
                  imageUrl: this.imageUrl(),
                  showModal: true,
                });
              }
            })
            .catch((error) => {
              (window as any).honeybadger?.notify(error?.response?.data?.error);
            });
        } else {
          this.setState(
            {
              submitting: false,
              selectedAnswer: null,
              answerState: response.data.state,
              enrolment: response.data.enrolment,
              markerProfilePictureUrls:
                response.data.marker_profile_picture_urls || [],
            },
            () => {
              this.setState({
                answerStateTagline: this.answerStateTagline(),
                imageUrl: this.imageUrl(),
                showModal: true,
              });
            },
          );
          refreshAnswers();
          getRecentNotifications();
          getXp();
        }
      })
      .catch((error) => {
        this.setState({ submitting: false });
        getRecentNotifications();
        errorNotification(
          "Error submitting answer. Please try again",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  /**
   * Interactive challenges own their own submit (autosave and submit share a
   * request), so they never pass through `submitAnswer` — which is where every
   * other question type raises the well-done modal. They get it from here
   * instead, off the answer the save endpoint returns.
   */
  handleInteractiveSubmitted = (answer) => {
    const { question, getRecentNotifications, getXp } = this.props;

    // Some challenges are their own reward — the life map reveals the PDF the
    // whole course builds towards, and it is also the lesson that completes the
    // course, so the modal that appeared was the full-screen certificate one,
    // sitting directly over the download. The XP and notification refreshes
    // still run; only the modal stands down.
    if (!question?.celebrates_in_page) {
      this.setState(
        {
          answerState: answer?.state || "",
          enrolment: answer?.enrolment || this.state.enrolment,
        },
        () => {
          this.setState({
            answerStateTagline: this.answerStateTagline(),
            imageUrl: this.imageUrl(),
            showModal: true,
          });
        },
      );
    }

    getRecentNotifications();
    getXp();
  };

  handleHideAnswerStateModal = (e) => {
    this.setState({ showModal: false });

    // The modal's dimmer locks body scroll, so the scroll Interactive asks for
    // on submit can land while it is open and do nothing. Ask again once the
    // learner has dismissed it and the answer is what they are looking at.
    if (this.props.mode === "Interactive") {
      window.setTimeout(() => this.scrollToAssignment(), 50);
    }
  };

  assignment = React.createRef<HTMLDivElement>();

  scrollToAssignment = () =>
    this.assignment.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  range = (max) => {
    return Array.from({ length: max }, (v, k) => k + 1);
  };

  toggleConfirmReset = (e) => {
    e.preventDefault();
    this.setState({ confirmReset: !this.state.confirmReset });
  };

  render() {
    const {
      mode,
      question,
      answer,
      loadingAnswer,
      currentUser,
      search,
      refreshAnswers,
      getRecentNotifications,
      getXp,
    } = this.props;

    const {
      selectedAnswer,
      submitting,
      showModal,
      answerState,
      redirectUrl,
      answerStateTagline,
      imageUrl,
      pollAnswerOptions,
      pollAnswerOptionsWithPercent,
      confirmReset,
      comments,
      enrolment,
      markerProfilePictureUrls,
      usingLife,
    } = this.state;

    return (
      <div className="current-lesson-assignment" ref={this.assignment}>
        {answer && enrolment && enrolment?.super_user && (
          <>
            <Button
              circular
              floated="right"
              icon="undo"
              className="reset"
              onClick={this.toggleConfirmReset}
            />
            <Confirm
              content="Reset this answer for realsies?"
              open={confirmReset}
              cancelButton="Never mind"
              confirmButton="Let's do it"
              onCancel={this.toggleConfirmReset}
              onConfirm={this.resetAnswer}
              size="mini"
            />
          </>
        )}
        {/* Interactive lessons are exercises, not questions, and each widget
            can name itself more precisely ("Creative Exercise"). */}
        <h3>{question?.challenge_title || "Question"}</h3>

        {question && (
          <>
            {redirectUrl && <Redirect to={{ pathname: redirectUrl }} />}
            {mode === "Choice" && (
              <Choice
                question={question}
                answer={answer}
                selectedAnswer={selectedAnswer}
                submitting={submitting}
                loadingAnswer={loadingAnswer}
                currentUser={currentUser}
                submitAnswer={this.submitAnswer}
                updateSelectedAnswer={this.updateSelectedAnswer}
              />
            )}
            {mode === "FileAction" && (
              <FileAction
                question={question}
                answer={answer}
                submitAnswer={this.submitAnswer}
                selectedAnswer={selectedAnswer}
                submitting={submitting}
                loadingAnswer={loadingAnswer}
                updateSelectedAnswer={this.updateSelectedAnswer}
                mode={mode}
                comments={comments}
                currentUser={currentUser}
                search={search}
                enrolmentId={enrolment?.id}
              />
            )}
            {mode === "Opinion" && (
              <Opinion
                enrolmentId={enrolment?.id}
                question={question}
                answer={answer}
                selectedAnswer={selectedAnswer}
                submitting={submitting}
                loadingAnswer={loadingAnswer}
                mode={mode}
                comments={comments}
                search={search}
                currentUser={currentUser}
                submitAnswer={this.submitAnswer}
                updateSelectedAnswer={this.updateSelectedAnswer}
              />
            )}
            {mode === "Poll" && (
              <Poll
                question={question}
                answer={answer}
                selectedAnswer={selectedAnswer}
                submitAnswer={this.submitAnswer}
                loadingAnswer={loadingAnswer}
                updateSelectedAnswer={this.updateSelectedAnswer}
                submitting={submitting}
                pollAnswerOptions={pollAnswerOptions}
                currentUser={currentUser}
                pollAnswerOptionsWithPercent={pollAnswerOptionsWithPercent}
              />
            )}
            {mode === "TextAction" && (
              <TextAction
                question={question}
                answer={answer}
                selectedAnswer={selectedAnswer}
                submitting={submitting}
                loadingAnswer={loadingAnswer}
                submitAnswer={this.submitAnswer}
                currentUser={currentUser}
                updateSelectedAnswer={this.updateSelectedAnswer}
              />
            )}
            {mode === "Interactive" && (
              <Interactive
                question={question}
                enrolment={enrolment}
                answer={answer}
                currentUser={currentUser}
                refreshAnswers={refreshAnswers}
                onSubmitted={this.handleInteractiveSubmitted}
              />
            )}
          </>
        )}
        {answer?.state === "failed" && enrolment && (
          <div className="q-card q-retry" role="status">
            {enrolment.lives_count > 0 ? (
              <>
                <p>
                  <b>Not quite.</b> Use a life to have another go at this one.
                </p>
                <button
                  type="button"
                  className={`btn btn-blue btn--compact ${
                    usingLife ? "loading" : ""
                  }`}
                  disabled={usingLife}
                  onClick={this.useLife}
                >
                  Use 1 life to try again
                </button>
              </>
            ) : (
              <p>
                <b>Not quite.</b> You have no lives left, so this answer cannot
                be changed.
              </p>
            )}
          </div>
        )}
        {enrolment && (
          <AnswerStateModal
            answerId={answer?.id}
            enrolment={enrolment}
            showModal={showModal}
            answerState={answerState}
            lesson={question}
            answerStateTagline={answerStateTagline}
            imageUrl={imageUrl}
            markerProfilePictureUrls={
              markerProfilePictureUrls.length > 0
                ? markerProfilePictureUrls
                : answer?.marker_profile_picture_urls
            }
            nextPath={nextLessonPath(question, this.props.lastLesson, enrolment)}
            handleHideAnswerStateModal={this.handleHideAnswerStateModal}
          />
        )}
      </div>
    );
  }
}

export default Assignment;
